/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  branch: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

function RpProductLocation() {
  const mod = 'rp_product_location';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'pd.code', label: 'สินค้า', align: 'left' },
    { id: 'tb.location_id', label: 'ตำแหน่ง', align: 'left' },
    { id: 'wh.name', label: 'คลัง', align: 'left' },
    { id: 'wh.branch_id', label: 'สาขา', align: 'left' },
    { id: 'tb.min_stock', label: 'MIN', align: 'right' },
    { id: 'tb.max_stock', label: 'MAX', align: 'right' },
    { id: 'pd.unit_name', label: 'หน่วย', align: 'left' },
    { id: 'tb.crated_at', label: 'วันที่สร้าง', align: 'center' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;

    window.open(`${urlExcel}/${param}`, '_self');
    // window.open(`${urlExcel}/${param}`, "_blank");
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setUrlExcel(result.excel);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สาขา :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={6}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_inventory'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left'>
                        <MDBox className='nowrap'>
                          {row.code} <span className='color-default'>{row.sku}</span>
                        </MDBox>
                        <MDBox className='cut' width={300}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell className='nowrap'>{row.location_name}</TableCell>
                      <TableCell className='nowrap'>{row.warehouse_name}</TableCell>
                      <TableCell className='nowrap'>{row.branch_name}</TableCell>
                      <TableCell align='right' className='nowrap color-danger'>
                        {Func.numberDisplay(row.min_stock)}
                      </TableCell>
                      <TableCell align='right' className='nowrap color-success'>
                        {Func.numberDisplay(row.max_stock)}
                      </TableCell>
                      <TableCell className='nowrap'>{row.unit_name}</TableCell>
                      <TableCell align='center' className='nowrap'>
                        {Func.dateTimeDisplay(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_inventory`), t(`menu.rp_product_location`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductLocation;
